import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { Upload, Search, Users, Building2, Tag, ChevronRight, Star, Phone, Mail, Calendar, Plus, X, Eye, Check, Trash, ChevronDown, ChevronUp, FileText, RefreshCw, MessageCircle, Send, AlertCircle, Brain } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const formatCurrency = (v) => new Intl.NumberFormat("en-IN").format(v || 0);

// ==================== RIOS DASHBOARD ====================
const RIOSDashboard = ({ onSelectContact }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/rios/dashboard`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-[#71717A]">Loading...</div>;

  const s = data?.summary || {};
  return (
    <div className="p-6 space-y-6" data-testid="rios-dashboard">
      <h1 className="text-2xl font-bold" style={{ fontFamily: "Varela Round" }}>Relationship Intelligence</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          ["Contacts", s.total_contacts, "#FFEB00"],
          ["Companies", s.total_companies, "#8B5CF6"],
          ["Brands", s.total_brands, "#3B82F6"],
          ["Starred", s.starred_contacts, "#F59E0B"],
          ["Tier A", s.tier_a_contacts, "#00C853"],
          ["Pending Review", s.pending_review, "#FF4444"],
        ].map(([label, val, color]) => (
          <div key={label} className="card-flat p-4">
            <span className="small-label">{label}</span>
            <p className="text-2xl font-bold mt-1" style={{ color }}>{val}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-flat p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Calendar size={18} className="text-[#FFEB00]" /> Upcoming Birthdays</h3>
          {(data?.upcoming_birthdays || []).length === 0 ? (
            <p className="text-sm text-[#71717A]">No birthdays this week</p>
          ) : (
            <div className="space-y-2">
              {data.upcoming_birthdays.map(c => (
                <div key={c.contact_id} className="flex items-center justify-between p-2 bg-[#1A1A00] border border-[#FFEB00]/20">
                  <div>
                    <span className="font-medium text-sm">{c.name}</span>
                    {c.company_name && <span className="text-xs text-[#71717A] ml-2">{c.company_name}</span>}
                  </div>
                  <span className="text-xs text-[#FFEB00]">{c.date_md}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card-flat p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Calendar size={18} className="text-[#8B5CF6]" /> Upcoming Anniversaries</h3>
          {(data?.upcoming_anniversaries || []).length === 0 ? (
            <p className="text-sm text-[#71717A]">No anniversaries this week</p>
          ) : (
            <div className="space-y-2">
              {data.upcoming_anniversaries.map(c => (
                <div key={c.contact_id} className="flex items-center justify-between p-2 bg-[#1A1A20] border border-[#8B5CF6]/20">
                  <div>
                    <span className="font-medium text-sm">{c.name}</span>
                    {c.company_name && <span className="text-xs text-[#71717A] ml-2">{c.company_name}</span>}
                  </div>
                  <span className="text-xs text-[#8B5CF6]">{c.date_md}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Latest Signals & Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-flat p-5" data-testid="dashboard-signals">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><AlertCircle size={18} className="text-[#FF4444]" /> Latest News & Alerts</h3>
          {(data?.recent_alerts || []).length === 0 ? (
            <p className="text-sm text-[#71717A]">No alerts yet. Run a Signal Scan from Settings or wait for the daily 9 AM / 7 PM scheduler.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {data.recent_alerts.map(a => (
                <div key={a.alert_id} className={`p-2 border text-sm ${a.status === "new" ? "border-[#FF4444]/30 bg-[#1A0A0A]" : "border-[#2A2A2A]"}`}>
                  <div className="flex items-center justify-between mb-1">
                    {a.url ? (
                      <a href={a.url} target="_blank" rel="noreferrer" className="font-medium text-xs hover:text-[#FFEB00] hover:underline">{a.title}</a>
                    ) : (
                      <span className="font-medium text-xs">{a.title}</span>
                    )}
                    <span className="text-[10px] text-[#71717A]">{new Date(a.date).toLocaleDateString()}</span>
                  </div>
                  <p className="text-xs text-[#A1A1AA]">{a.summary}</p>
                  {(a.linked_contact_name || a.linked_company_name) && (
                    <p className="text-[10px] text-[#FFEB00] mt-1">{a.linked_contact_name || a.linked_company_name}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card-flat p-5" data-testid="dashboard-gmail-recent">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Mail size={18} className="text-[#00C853]" /> Recent Gmail Interactions</h3>
          {(data?.recent_gmail_interactions || []).length === 0 ? (
            <p className="text-sm text-[#71717A]">No recent Gmail interactions. Connect Gmail in Settings and run a scan.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {data.recent_gmail_interactions.map(c => (
                <div key={c.contact_id} className="p-2 border border-[#2A2A2A] hover:border-[#00C853]/30">
                  <div className="flex items-center justify-between mb-1 cursor-pointer" onClick={() => onSelectContact && onSelectContact(c.contact_id)}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{c.name}</p>
                      <p className="text-xs text-[#71717A] truncate">{c.email}{c.company_name ? ` · ${c.company_name}` : ""}</p>
                    </div>
                    <div className="text-right ml-2">
                      <p className="text-xs text-[#00C853]">{new Date(c.last_interaction_date).toLocaleDateString()}</p>
                      <p className="text-[10px] text-[#71717A]">{c.last_interaction_direction === "sent" ? "you sent" : "received"}</p>
                    </div>
                  </div>
                  {c.last_interaction_subject && (
                    <p className="text-xs font-medium text-[#FFEB00] truncate mt-1">{c.last_interaction_subject}</p>
                  )}
                  {c.last_interaction_snippet && (
                    <p className="text-[10px] text-[#A1A1AA] line-clamp-2">{c.last_interaction_snippet}</p>
                  )}
                  {c.last_interaction_thread_id && (
                    <a
                      href={`https://mail.google.com/mail/u/0/#inbox/${c.last_interaction_thread_id}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="text-[10px] text-[#3B82F6] hover:underline inline-block mt-1"
                    >Open in Gmail ↗</a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== INLINE EDITABLE CELL ====================
const EditableCell = ({ value, field, contactId, onSave, type = "text", options }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const save = async () => {
    if (val !== (value || "")) await onSave(contactId, field, val);
    setEditing(false);
  };
  if (editing) {
    if (options) return (
      <select className="input-default py-0.5 px-1 text-xs w-full" value={val} autoFocus
        onChange={e => { setVal(e.target.value); }}
        onBlur={() => { onSave(contactId, field, val); setEditing(false); }}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
    return <input type={type} className="input-default py-0.5 px-1 text-xs w-full" value={val} autoFocus
      onChange={e => setVal(e.target.value)} onBlur={save} onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }} />;
  }
  return <span onClick={e => { e.stopPropagation(); setEditing(true); }} className="cursor-text hover:bg-[#1A1A1A] px-1 py-0.5 -mx-1 rounded inline-block min-w-[40px]" title="Click to edit">
    {value || <span className="text-[#333]">—</span>}
  </span>;
};

// ==================== MERGE MODAL ====================
const MergeModal = ({ contacts, onClose, onCommit }) => {
  const [merged, setMerged] = useState(null);
  const [primaryId, setPrimaryId] = useState(contacts[0]?.contact_id || "");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.post(`${API}/rios/contacts/merge-preview`, { contact_ids: contacts.map(c => c.contact_id) })
      .then(r => { setMerged(r.data.merged); setLoading(false); })
      .catch(err => { alert("Merge preview failed"); onClose(); });
  }, [contacts, onClose]);

  const setField = (k, v) => setMerged(prev => ({ ...prev, [k]: v }));

  const commit = async () => {
    const deleteIds = contacts.filter(c => c.contact_id !== primaryId).map(c => c.contact_id);
    await axios.post(`${API}/rios/contacts/merge-commit`, { primary_id: primaryId, delete_ids: deleteIds, merged_data: merged });
    onCommit();
  };

  if (loading || !merged) return <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center"><p className="text-[#71717A]">Loading merge preview...</p></div>;

  const fields = [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["company_name", "Company"], ["brand_name", "Brand"], ["designation", "Designation"], ["tier", "Tier"], ["birthday", "Birthday"], ["anniversary", "Anniversary"], ["linkedin_url", "LinkedIn"]];

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="merge-modal">
      <div className="bg-[#111] border border-[#2A2A2A] max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-[#2A2A2A] flex items-center justify-between sticky top-0 bg-[#111] z-10">
          <h3 className="text-lg font-bold">Merge {contacts.length} Contacts</h3>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button onClick={commit} className="btn-primary text-sm" data-testid="commit-merge-btn">Approve & Merge</button>
          </div>
        </div>
        <div className="p-4">
          <div className="mb-4">
            <label className="form-label mb-2">Keep as primary contact:</label>
            <select className="input-default" value={primaryId} onChange={e => setPrimaryId(e.target.value)}>
              {contacts.map(c => <option key={c.contact_id} value={c.contact_id}>{c.name} ({c.email || c.phone || "no id"})</option>)}
            </select>
          </div>
          <table className="table-dense w-full">
            <thead>
              <tr><th>Field</th>{contacts.map((c, i) => <th key={i} className="text-xs">{c.name?.split(" ")[0] || `Contact ${i+1}`}</th>)}<th className="text-[#FFEB00]">Merged Result</th></tr>
            </thead>
            <tbody>
              {fields.map(([key, label]) => (
                <tr key={key}>
                  <td className="font-medium text-xs">{label}</td>
                  {contacts.map((c, i) => (
                    <td key={i} className="text-xs">
                      <button className={`text-left w-full px-1 py-0.5 hover:bg-[#1A1A00] rounded ${merged[key] === (c[key] || "") ? "bg-[#FFEB00]/20 text-[#FFEB00]" : "text-[#A1A1AA]"}`}
                        onClick={() => setField(key, c[key] || "")}>
                        {c[key] || <span className="text-[#333]">—</span>}
                      </button>
                    </td>
                  ))}
                  <td>
                    <input className="input-default py-0.5 px-1 text-xs w-full bg-[#1A1A00] border-[#FFEB00]/30" value={merged[key] || ""}
                      onChange={e => setField(key, e.target.value)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-[#71717A] mt-3">Click any source value to use it. Edit the merged result directly. The primary contact will be updated, others will be deleted.</p>
        </div>
      </div>
    </div>
  );
};

// ==================== CONTACTS LIST ====================
const ContactsList = ({ onSelectContact }) => {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [letterFilter, setLetterFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncingCRM, setSyncingCRM] = useState(false);
  const [converting, setConverting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showMerge, setShowMerge] = useState(false);
  const [syncSinceDate, setSyncSinceDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return d.toISOString().split("T")[0];
  });
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const sortedContacts = [...contacts].sort((a, b) => {
    const av = (a[sortBy] || "").toString().toLowerCase();
    const bv = (b[sortBy] || "").toString().toLowerCase();
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const sortArrow = (col) => sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : "";

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (tierFilter) params.set("tier", tierFilter);
    if (letterFilter) params.set("letter", letterFilter);
    const r = await axios.get(`${API}/rios/contacts?${params}`);
    setContacts(r.data.contacts); setTotal(r.data.total); setLoading(false);
    setSelectedIds(new Set());
  }, [search, tierFilter, letterFilter]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const inlineEdit = async (contactId, field, value) => {
    await axios.put(`${API}/rios/contacts/${contactId}`, { [field]: value });
    setContacts(prev => prev.map(c => c.contact_id === contactId ? { ...c, [field]: value } : c));
  };

  const syncCRM = async () => {
    try {
      const conn = await axios.get(`${API}/zoho-crm/connection`);
      if (!conn.data.connected) {
        if (window.confirm("Zoho CRM not connected.\n\nGo to Settings to connect?")) window.location.href = "/settings";
        return;
      }
    } catch {}
    setSyncingCRM(true);
    try {
      const r = await axios.post(`${API}/zoho-crm/sync`, { since_date: syncSinceDate });
      if (r.data.status === "started" || r.data.status === "already_running") {
        const poll = setInterval(async () => {
          try {
            const s = await axios.get(`${API}/zoho-crm/sync-status`);
            if (!s.data.running) {
              clearInterval(poll);
              setSyncingCRM(false);
              const res = s.data.result || {};
              if (res.error) alert("CRM Sync Error: " + res.error);
              else alert(`CRM Sync Done: ${res.created || 0} new, ${res.updated || 0} updated, ${res.skipped || 0} skipped`);
              fetchContacts();
            }
          } catch {}
        }, 3000);
      }
    } catch (err) {
      setSyncingCRM(false);
      const d = err.response?.data?.detail || "";
      if (d.includes("not connected") || err.response?.status === 401) {
        if (window.confirm("Zoho CRM not connected.\n\nGo to Settings?")) window.location.href = "/settings";
      } else alert("CRM Sync failed: " + (d || err.message));
    }
  };

  const convertToCompanies = async () => {
    if (!window.confirm("Move company-like contacts into Companies list?\n\n(Contacts where name = company name will be removed from contacts)")) return;
    setConverting(true);
    try {
      const r = await axios.post(`${API}/rios/contacts/convert-to-companies`);
      alert(`${r.data.companies_created} companies created, ${r.data.contacts_removed} removed`);
      fetchContacts();
    } catch (err) { alert("Failed: " + (err.response?.data?.detail || err.message)); }
    finally { setConverting(false); }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} contact(s)?`)) return;
    await axios.post(`${API}/rios/contacts/bulk-delete`, { contact_ids: Array.from(selectedIds) });
    fetchContacts();
  };

  const deleteSingle = async (e, id) => { e.stopPropagation(); if (!window.confirm("Delete?")) return; await axios.delete(`${API}/rios/contacts/${id}`); fetchContacts(); };
  const toggleSelect = (e, id) => { e.stopPropagation(); setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const toggleAll = () => { if (selectedIds.size === contacts.length) setSelectedIds(new Set()); else setSelectedIds(new Set(contacts.map(c => c.contact_id))); };
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  const selectedContacts = contacts.filter(c => selectedIds.has(c.contact_id));

  return (
    <div className="p-6 space-y-4" data-testid="contacts-list">
      {showMerge && selectedContacts.length >= 2 && (
        <MergeModal contacts={selectedContacts} onClose={() => setShowMerge(false)} onCommit={() => { setShowMerge(false); fetchContacts(); }} />
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Varela Round" }}>Contacts <span className="text-[#71717A] text-base font-normal">({total})</span></h1>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedIds.size >= 2 && (
            <button onClick={() => setShowMerge(true)} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white px-3 py-2 text-sm font-medium flex items-center gap-1" data-testid="merge-btn">
              Merge ({selectedIds.size})
            </button>
          )}
          {selectedIds.size > 0 && (
            <button onClick={deleteSelected} className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 text-sm font-medium flex items-center gap-1" data-testid="bulk-delete-btn">
              <Trash size={14} /> Delete ({selectedIds.size})
            </button>
          )}
          <button onClick={convertToCompanies} disabled={converting} className="btn-secondary flex items-center gap-2 text-sm" data-testid="convert-companies-btn">
            <Building2 size={16} /> {converting ? "..." : "To Companies"}
          </button>
          <a
            href="#"
            onClick={async (e) => {
              e.preventDefault();
              try {
                const params = new URLSearchParams();
                if (search) params.set("search", search);
                if (tierFilter) params.set("tier", tierFilter);
                if (letterFilter) params.set("letter", letterFilter);
                const q = params.toString();
                const res = await axios.get(
                  `${process.env.REACT_APP_BACKEND_URL}/api/rios/contacts/export.csv${q ? `?${q}` : ""}`,
                  { responseType: "blob" }
                );
                const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `rios_contacts_${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (err) {
                alert(err.response?.data?.detail || err.message || "Export failed");
              }
            }}
            className="btn-secondary flex items-center gap-2 text-sm"
            data-testid="export-contacts-csv-btn"
            title="Download the full contact list (respects current filters) as CSV"
          >
            <FileText size={16} /> Export CSV
          </a>
          <button onClick={() => onSelectContact("new")} className="btn-primary flex items-center gap-2" data-testid="add-contact-btn"><Plus size={16} /> Add</button>
        </div>
      </div>

      {/* CRM Sync with date picker */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-[#71717A]">Sync CRM from:</span>
        <input type="date" className="input-default py-1 px-2 text-xs" value={syncSinceDate} onChange={e => setSyncSinceDate(e.target.value)} data-testid="sync-since-date" />
        <button onClick={syncCRM} disabled={syncingCRM} className="btn-secondary flex items-center gap-2 text-xs" data-testid="sync-crm-btn">
          <RefreshCw size={14} className={syncingCRM ? "animate-spin" : ""} /> {syncingCRM ? "Syncing..." : "Sync CRM"}
        </button>
      </div>

      <div className="flex items-center gap-0.5 flex-wrap" data-testid="alpha-filter">
        <button onClick={() => setLetterFilter("")} className={`px-2 h-8 text-xs font-medium transition-colors ${!letterFilter ? "bg-[#FFEB00] text-black" : "bg-[#1A1A1A] text-[#71717A] hover:text-white"}`}>All</button>
        {letters.map(l => (
          <button key={l} onClick={() => setLetterFilter(letterFilter === l ? "" : l)}
            className={`w-7 h-8 text-xs font-medium transition-colors ${letterFilter === l ? "bg-[#FFEB00] text-black" : "bg-[#1A1A1A] text-[#A1A1AA] hover:text-white hover:bg-[#2A2A2A]"}`}>{l}</button>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <div className="relative flex-1"><Search size={16} className="absolute left-3 top-3 text-[#71717A]" />
          <input className="input-default w-full pl-10" placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} data-testid="search-contacts" /></div>
        <select className="input-default" value={tierFilter} onChange={e => setTierFilter(e.target.value)}><option value="">All Tiers</option><option value="A">Tier A</option><option value="B">Tier B</option><option value="C">Tier C</option></select>
      </div>
      {loading ? <p className="text-[#71717A]">Loading...</p> : (
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th><input type="checkbox" checked={selectedIds.size === contacts.length && contacts.length > 0} onChange={toggleAll} className="accent-[#FFEB00]" /></th>
              <th onClick={() => toggleSort("name")} className="cursor-pointer hover:text-[#FFEB00] select-none" data-testid="sort-name">Name{sortArrow("name")}</th>
              <th onClick={() => toggleSort("company_name")} className="cursor-pointer hover:text-[#FFEB00] select-none" data-testid="sort-company">Company{sortArrow("company_name")}</th>
              <th onClick={() => toggleSort("email")} className="cursor-pointer hover:text-[#FFEB00] select-none" data-testid="sort-email">Email{sortArrow("email")}</th>
              <th onClick={() => toggleSort("phone")} className="cursor-pointer hover:text-[#FFEB00] select-none" data-testid="sort-phone">Phone{sortArrow("phone")}</th>
              <th onClick={() => toggleSort("tier")} className="cursor-pointer hover:text-[#FFEB00] select-none" data-testid="sort-tier">Tier{sortArrow("tier")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedContacts.map(c => (
              <tr key={c.contact_id} className="hover:bg-[#1A1A1A]/50 group">
                <td><input type="checkbox" checked={selectedIds.has(c.contact_id)} onChange={e => toggleSelect(e, c.contact_id)} className="accent-[#FFEB00]" /></td>
                <td className="font-medium text-sm"><EditableCell value={c.name} field="name" contactId={c.contact_id} onSave={inlineEdit} /></td>
                <td className="text-sm"><EditableCell value={c.company_name} field="company_name" contactId={c.contact_id} onSave={inlineEdit} /></td>
                <td className="text-sm text-[#A1A1AA]"><EditableCell value={c.email} field="email" contactId={c.contact_id} onSave={inlineEdit} type="email" /></td>
                <td className="text-sm text-[#A1A1AA]"><EditableCell value={c.phone} field="phone" contactId={c.contact_id} onSave={inlineEdit} type="tel" /></td>
                <td><EditableCell value={c.tier} field="tier" contactId={c.contact_id} onSave={inlineEdit} options={["A","B","C"]} /></td>
                <td className="flex items-center gap-1">
                  <button onClick={() => onSelectContact(c.contact_id)} className="text-[#71717A] hover:text-white p-1" title="Open profile"><Eye size={14} /></button>
                  <button onClick={e => deleteSingle(e, c.contact_id)} className="text-[#FF4444] hover:text-[#FF6666] p-1 opacity-0 group-hover:opacity-100" title="Delete"><Trash size={14} /></button>
                </td>
              </tr>
            ))}
            {contacts.length === 0 && <tr><td colSpan={7} className="text-center text-[#71717A] py-8">No contacts {letterFilter ? `starting with "${letterFilter}"` : "found"}</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ==================== CONTACT PROFILE / FORM ====================
const ContactProfile = ({ contactId, onBack }) => {
  const [contact, setContact] = useState(null);
  const [editing, setEditing] = useState(contactId === "new");
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(contactId !== "new");
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [brands, setBrands] = useState([]);
  const [signals, setSignals] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [enrichment, setEnrichment] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [enrichmentAccepted, setEnrichmentAccepted] = useState({});
  const [editingKeywords, setEditingKeywords] = useState(false);
  const [keywordsDraft, setKeywordsDraft] = useState("");
  const [keywordsSaving, setKeywordsSaving] = useState(false);

  const saveKeywordsInline = async () => {
    setKeywordsSaving(true);
    try {
      const r = await axios.put(`${API}/rios/contacts/${contactId}`, { signal_keywords: keywordsDraft });
      setContact(r.data.contact);
      setForm(r.data.contact);
      setEditingKeywords(false);
    } catch (err) {
      alert("Save failed: " + (err.response?.data?.detail || err.message));
    } finally { setKeywordsSaving(false); }
  };

  useEffect(() => {
    if (contactId !== "new") {
      axios.get(`${API}/rios/contacts/${contactId}`).then(r => { setContact(r.data.contact); setForm(r.data.contact); setLoading(false); });
      axios.get(`${API}/rios/signals?contact_id=${contactId}`).then(r => setSignals(r.data.signals || [])).catch(() => {});
      axios.get(`${API}/rios/alerts?contact_id=${contactId}`).then(r => setAlerts(r.data.alerts || [])).catch(() => {});
    } else {
      setForm({ name: "", email: "", phone: "", company_name: "", brand_name: "", tier: "C", relationship_type: "", designation: "", birthday: "", anniversary: "", spouse_name: "", kids: [], preferences: "", gifting_notes: "", personal_context: "", linkedin_url: "", starred: false, personal_contact: false, office_contact: true });
    }
    axios.get(`${API}/rios/companies`).then(r => setCompanies([...(r.data.companies || []), ...(r.data.zoho_companies || [])]));
    axios.get(`${API}/rios/brands`).then(r => setBrands(r.data.brands || []));
  }, [contactId]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (contactId === "new") {
        await axios.post(`${API}/rios/contacts`, form);
      } else {
        await axios.put(`${API}/rios/contacts/${contactId}`, form);
      }
      onBack();
    } catch (err) { alert(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const question = chatInput;
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: question }]);
    setChatLoading(true);
    try {
      const r = await axios.post(`${API}/rios/ai-chat`, { question, contact_id: contactId, session_id: `chat_${contactId}` });
      setChatMessages(prev => [...prev, { role: "assistant", content: r.data.response }]);
    } catch (err) { setChatMessages(prev => [...prev, { role: "assistant", content: "Error: " + (err.response?.data?.error || err.message) }]); }
    finally { setChatLoading(false); }
  };

  const runEnrichment = async () => {
    if (enriching || contactId === "new") return;
    setEnriching(true);
    setEnrichment(null);
    try {
      // Always bypass cache on manual trigger — Apollo plan / contact data may have changed
      const r = await axios.post(`${API}/rios/contacts/${contactId}/enrich`, { use_cache: false });
      setEnrichment(r.data);
    } catch (err) {
      alert("Enrichment failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setEnriching(false);
    }
  };

  const applyOneField = async (field, value) => {
    try {
      const r = await axios.post(`${API}/rios/contacts/${contactId}/enrich/apply`, {
        fields: { [field]: value },
        source: enrichment?.source_summary || "enrichment",
      });
      setContact(r.data.contact);
      setForm(r.data.contact);
      // Remove accepted field from suggestions
      setEnrichment(prev => {
        if (!prev) return prev;
        const ns = { ...prev.suggested };
        delete ns[field];
        return { ...prev, suggested: ns };
      });
    } catch (err) {
      alert("Apply failed: " + (err.response?.data?.detail || err.message));
    }
  };

  const applyAllEnrichment = async () => {
    if (!enrichment?.suggested) return;
    const fields = Object.fromEntries(Object.entries(enrichment.suggested).map(([k, v]) => [k, v.suggested]));
    try {
      const r = await axios.post(`${API}/rios/contacts/${contactId}/enrich/apply`, {
        fields, source: enrichment.source_summary || "enrichment",
      });
      setContact(r.data.contact);
      setForm(r.data.contact);
      setEnrichment(prev => prev ? { ...prev, suggested: {} } : prev);
    } catch (err) {
      alert("Apply failed: " + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) return <div className="p-8 text-[#71717A]">Loading...</div>;

  const isView = !editing;
  const d = isView ? contact : form;
  if (!d) return null;

  // Compute initials
  const initials = (d.name || "?").split(" ").filter(Boolean).slice(0, 2).map(s => s[0]).join("").toUpperCase();
  const tierColor = d.tier === "A" ? "bg-[#00C853] text-[#0A0A0A]" : d.tier === "B" ? "bg-[#FFEB00] text-[#0A0A0A]" : "bg-[#2A2A2A] text-[#A1A1AA]";

  return (
    <div className="p-6 space-y-6" data-testid="contact-profile">
      {/* Premium Hero Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => {
            if (!isView) {
              // If editing, cancel edit rather than navigating away
              if (contactId === "new") { onBack(); } else { setEditing(false); setForm(contact); }
            } else {
              onBack();
            }
          }}
          className="btn-ghost text-sm"
          data-testid="profile-back-btn"
        >&larr; Back</button>
        <div className="flex-1" />
        {isView ? (
          <div className="flex gap-2">
            {contactId !== "new" && (
              <>
                <button onClick={() => setChatOpen(true)} className="btn-primary text-sm flex items-center gap-1.5" data-testid="ai-chat-btn">
                  <Brain size={14} /> AI Chat
                </button>
                <button onClick={runEnrichment} disabled={enriching} className="btn-secondary text-sm" data-testid="enrich-contact-btn">
                  {enriching ? "Enriching..." : "Enrich"}
                </button>
              </>
            )}
            <button onClick={() => { setEditing(true); setForm(contact); }} className="btn-secondary text-sm" data-testid="edit-contact-btn">Edit</button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => { if (contactId === "new") { onBack(); } else { setEditing(false); } }} className="btn-secondary text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="save-contact-btn">{saving ? "Saving..." : "Save"}</button>
          </div>
        )}
      </div>

      {isView && (
        <div className="flex items-center gap-5 pb-6 border-b border-[#2A2A2A]">
          {/* Initials Avatar */}
          <div className="w-20 h-20 flex items-center justify-center text-2xl font-bold bg-gradient-to-br from-[#FFEB00]/20 to-[#8B5CF6]/20 border border-[#FFEB00]/30 text-[#FFEB00]" style={{ fontFamily: "Varela Round" }}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-4xl sm:text-5xl font-bold truncate" style={{ fontFamily: "Varela Round" }}>{d.name || "New Contact"}</h1>
              {d.starred && <Star size={22} className="text-[#F59E0B] fill-[#F59E0B] shrink-0" />}
              <span className={`text-xs px-2.5 py-1 font-bold rounded-sm ${tierColor}`} data-testid="tier-badge">TIER {d.tier || "C"}</span>
            </div>
            {d.headline && <p className="text-sm text-[#A1A1AA] mb-1.5 italic">{d.headline}</p>}
            {(d.designation || d.company_name) && (
              <p className="text-sm text-[#E4E4E7]">
                {d.designation && <span className="font-medium">{d.designation}</span>}
                {d.designation && d.company_name && <span className="text-[#71717A] mx-1.5">·</span>}
                {d.company_name && <span>{d.company_name}</span>}
              </p>
            )}
          </div>
        </div>
      )}

      {enrichment && isView && (
        <div className="card-flat p-5 border border-[#FFEB00]/40 bg-[#1A1A00]/30" data-testid="enrichment-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <RefreshCw size={16} className="text-[#FFEB00]" />
              Suggested Enrichment
              {enrichment.confidence_score > 0 && (
                <span className="text-xs text-[#A1A1AA]">· confidence {enrichment.confidence_score}%</span>
              )}
              {enrichment.cached && <span className="text-xs text-[#71717A]">· cached {enrichment.age_hours}h ago</span>}
            </h3>
            <div className="flex gap-2">
              {Object.keys(enrichment.suggested || {}).length > 0 && (
                <button onClick={applyAllEnrichment} className="btn-primary text-xs" data-testid="enrich-accept-all">Accept All</button>
              )}
              <button onClick={() => { setEnrichment(null); setEnrichmentAccepted({}); }} className="btn-secondary text-xs">Ignore</button>
            </div>
          </div>
          {enrichment.message && (
            <p className="text-sm text-[#FF4444] mb-2">{enrichment.message}</p>
          )}
          {enrichment.apollo_enabled === false && (
            <p className="text-xs text-[#71717A] mb-2">Apollo not configured (using Gmail + AI only)</p>
          )}
          {enrichment.apollo_error && (
            <p className="text-xs text-[#FF4444] mb-2">Apollo error: {enrichment.apollo_error}</p>
          )}
          {enrichment.apollo_enabled && enrichment.apollo_found && enrichment.apollo_is_thin && (
            <div className="text-xs text-[#71717A] mb-2 p-2 border border-[#2A2A2A] bg-[#0F0F0F]">
              Apollo confirmed this person exists but returned minimal fields (name only). This typically happens when the contact has a sparse public profile.
            </div>
          )}
          {enrichment.apollo_enabled && !enrichment.apollo_found && (
            <p className="text-xs text-[#71717A] mb-2">Apollo did not find a matching person for this name + company.</p>
          )}
          {enrichment.reasoning && (
            <p className="text-xs text-[#71717A] mb-2 italic">AI: {enrichment.reasoning}</p>
          )}
          {Object.keys(enrichment.suggested || {}).length === 0 ? (
            <p className="text-sm text-[#71717A]">No new fields suggested.</p>
          ) : (
            <div className="space-y-3">
              {/* Group by category */}
              {[
                { title: "Identity", fields: ["email", "phone", "linkedin_url"] },
                { title: "Professional", fields: ["designation", "company_name", "company_website", "seniority", "headline"] },
                { title: "Location", fields: ["city", "country"] },
              ].map(group => {
                const groupEntries = Object.entries(enrichment.suggested).filter(([f]) => group.fields.includes(f));
                if (groupEntries.length === 0) return null;
                return (
                  <div key={group.title}>
                    <p className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1.5">{group.title}</p>
                    <div className="space-y-1.5">
                      {groupEntries.map(([field, info]) => (
                        <div key={field} className="flex items-center gap-3 p-2 border border-[#2A2A2A] bg-[#0F0F0F]">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-[10px] text-[#71717A] uppercase tracking-wide">{field.replace("_", " ")}</p>
                              {!info.current && <span className="text-[9px] px-1 py-0.5 bg-[#FF4444]/15 text-[#FF4444]">MISSING</span>}
                            </div>
                            <p className="text-sm truncate">
                              {info.current && <span className="text-[#71717A] line-through mr-1">{info.current}</span>}
                              <span className="text-[#FFEB00] font-medium">{info.suggested}</span>
                            </p>
                          </div>
                          <button onClick={() => applyOneField(field, info.suggested)} className="btn-primary text-[10px] px-2 py-1" data-testid={`enrich-accept-${field}`}>Accept</button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {enrichment.alt_emails?.length > 0 && (
            <div className="mt-3 p-2 border border-[#2A2A2A]">
              <p className="text-xs text-[#71717A] uppercase tracking-wide mb-2">Alternative emails</p>
              <div className="space-y-1">
                {enrichment.alt_emails.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="flex-1 text-[#A1A1AA] truncate">{e}</span>
                    <button onClick={() => applyOneField("email", e)} className="btn-primary text-[10px] px-2 py-1">Use</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {enrichment.apollo_data && (
            <details className="mt-3">
              <summary className="text-xs text-[#8B5CF6] cursor-pointer hover:underline">
                View full Apollo result — click any field to apply
                {enrichment.apollo_email_looks_mismatched && (
                  <span className="text-[10px] text-[#FF4444] ml-2">(existing email seemed mismatched — Apollo was queried by name + company)</span>
                )}
              </summary>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1">
                {Object.entries(enrichment.apollo_data).filter(([_, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-xs p-2 border border-[#2A2A2A]">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-[#71717A] uppercase">{k.replace("_", " ")}</p>
                      <p className="text-[#A1A1AA] truncate">{v}</p>
                    </div>
                    {["email","phone","designation","linkedin_url","company","company_website","city","country","seniority","headline"].includes(k) && (
                      <button
                        onClick={() => applyOneField(k === "company" ? "company_name" : k, v)}
                        className="btn-primary text-[10px] px-2 py-1"
                      >Use</button>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
          {enrichment.web_results?.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-[#00C853] cursor-pointer hover:underline">View {enrichment.web_results.length} web search results — apply any URL to a field</summary>
              <div className="mt-2 space-y-1">
                {enrichment.web_results.map((r, i) => (
                  <div key={i} className="text-xs p-2 border border-[#2A2A2A]">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <a href={r.url} target="_blank" rel="noreferrer" className="text-[#3B82F6] hover:underline font-medium truncate block">{r.title}</a>
                        <p className="text-[10px] text-[#71717A] truncate">{r.url}</p>
                        {r.snippet && <p className="text-[11px] text-[#A1A1AA] line-clamp-2 mt-1">{r.snippet}</p>}
                      </div>
                      <select
                        defaultValue=""
                        onChange={e => {
                          const field = e.target.value;
                          if (field) { applyOneField(field, r.url); e.target.value = ""; }
                        }}
                        className="text-[10px] bg-[#0A0A0A] border border-[#333] px-1 py-0.5 text-[#FFEB00]"
                        data-testid={`web-result-apply-${i}`}
                      >
                        <option value="">Use for...</option>
                        <option value="linkedin_url">LinkedIn</option>
                        <option value="x_url">X / Twitter</option>
                        <option value="instagram_url">Instagram</option>
                        <option value="facebook_url">Facebook</option>
                        <option value="company_website">Website</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
          {enrichment.gmail_candidates?.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-[#3B82F6] cursor-pointer hover:underline">View {enrichment.gmail_candidates.length} Gmail email candidates — click to apply any</summary>
              <div className="mt-2 space-y-1">
                {enrichment.gmail_candidates.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs p-2 border border-[#2A2A2A] hover:border-[#FFEB00]/40">
                    <div className="flex-1 min-w-0">
                      <p className="text-[#A1A1AA] truncate">{c.email}</p>
                      <p className="text-[10px] text-[#71717A]">
                        {c.display_name && <span>{c.display_name} · </span>}
                        from {c.header}
                      </p>
                    </div>
                    <button
                      onClick={() => applyOneField("email", c.email)}
                      className="btn-primary text-[10px] px-2 py-1"
                      data-testid={`enrich-use-email-${i}`}
                    >Use</button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
        {/* Left: 70% */}
        <div className="lg:col-span-7 space-y-5">
          <div className="card-flat p-5 border-l-2 border-l-[#FFEB00]/40">
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-[#A1A1AA]">Identity</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                ["Name", "name", "text"],
                ["Email", "email", "email"],
                ["Phone", "phone", "tel"],
                ["Alternate Email", "alternate_email", "email"],
                ["Alternate Phone", "alternate_phone", "tel"],
              ].map(([label, key, type]) => (
                <div key={key} className="form-group">
                  <label className="form-label">{label}</label>
                  {isView ? <p className="text-sm mt-1 font-medium text-[#E4E4E7]">{d[key] || <span className="text-[#71717A]">—</span>}</p> :
                    <input type={type} className="input-default w-full" value={form[key] || ""} onChange={e => set(key, e.target.value)} />}
                </div>
              ))}
              <div className="form-group">
                <label className="form-label">Tier</label>
                {isView ? <p className="text-sm mt-1 font-medium text-[#E4E4E7]">Tier {d.tier || "C"}</p> :
                  <select className="input-default w-full" value={form.tier || "C"} onChange={e => set("tier", e.target.value)}>
                    <option value="A">A</option><option value="B">B</option><option value="C">C</option>
                  </select>}
              </div>
              <div className="form-group col-span-2">
                <label className="form-label">Relationship Type</label>
                {isView ? <p className="text-sm mt-1 font-medium text-[#E4E4E7]">{d.relationship_type || <span className="text-[#71717A]">—</span>}</p> :
                  <input className="input-default w-full" value={form.relationship_type || ""} onChange={e => set("relationship_type", e.target.value)} />}
              </div>
            </div>
            {!isView && (
              <div className="flex items-center gap-6 mt-4 pt-4 border-t border-[#2A2A2A] flex-wrap">
                {[["starred", "Starred"], ["personal_contact", "Personal"], ["office_contact", "Office"], ["track_signals", "Track Signals"]].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={form[key] || false} onChange={e => set(key, e.target.checked)} className="accent-[#FFEB00]" />
                    {label}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Professional Overview */}
          <div className="card-flat p-5 border-l-2 border-l-[#8B5CF6]/40">
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-[#A1A1AA]">Professional Overview</h3>
            {isView && d.headline && (
              <div className="mb-4 pb-4 border-b border-[#2A2A2A]">
                <p className="text-base italic text-[#E4E4E7] leading-relaxed">{d.headline}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              {!isView && (
                <div className="form-group col-span-2">
                  <label className="form-label">Headline</label>
                  <input className="input-default w-full" value={form.headline || ""} onChange={e => set("headline", e.target.value)} placeholder="e.g. Founder & CEO at Acme Corp" />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Designation</label>
                {isView ? <p className="text-sm mt-1 font-medium text-[#E4E4E7]">{d.designation || <span className="text-[#71717A]">—</span>}</p> :
                  <input className="input-default w-full" value={form.designation || ""} onChange={e => set("designation", e.target.value)} />}
              </div>
              <div className="form-group">
                <label className="form-label">Seniority</label>
                {isView ? <p className="text-sm mt-1 font-medium text-[#E4E4E7] capitalize">{d.seniority || <span className="text-[#71717A]">—</span>}</p> :
                  <input className="input-default w-full" value={form.seniority || ""} onChange={e => set("seniority", e.target.value)} placeholder="e.g. c_suite, vp, director" />}
              </div>
              <div className="form-group">
                <label className="form-label">Company</label>
                {isView ? <p className="text-sm mt-1 font-medium text-[#E4E4E7]">{d.company_name || <span className="text-[#71717A]">—</span>}</p> :
                  <div className="relative">
                    <input className="input-default w-full" value={form.company_name || ""} onChange={e => set("company_name", e.target.value)} list="company-list-pro" placeholder="Type or select company" />
                    <datalist id="company-list-pro">{companies.map(c => <option key={c.company_id || c.name} value={c.name} />)}</datalist>
                  </div>}
              </div>
              <div className="form-group">
                <label className="form-label">Brand</label>
                {isView ? <p className="text-sm mt-1 font-medium text-[#E4E4E7]">{d.brand_name || <span className="text-[#71717A]">—</span>}</p> :
                  <input className="input-default w-full" value={form.brand_name || ""} onChange={e => set("brand_name", e.target.value)} list="brand-list-pro" />}
                <datalist id="brand-list-pro">{brands.map(b => <option key={b.brand_id} value={b.name} />)}</datalist>
              </div>
              <div className="form-group col-span-2">
                <label className="form-label">Location</label>
                {isView ? (
                  <p className="text-sm mt-1 font-medium text-[#E4E4E7]">
                    {[d.city, d.country].filter(Boolean).join(", ") || <span className="text-[#71717A]">—</span>}
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <input className="input-default w-full" value={form.city || ""} onChange={e => set("city", e.target.value)} placeholder="City" />
                    <input className="input-default w-full" value={form.country || ""} onChange={e => set("country", e.target.value)} placeholder="Country" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Personal Memory */}
          <div className="card-flat p-5">
            <h3 className="font-semibold mb-4">Personal Memory</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                ["Birthday", "birthday"],
                ["Anniversary", "anniversary"],
                ["Spouse Name", "spouse_name"],
                ["Spouse Birthday", "spouse_birthday"],
              ].map(([label, key]) => (
                <div key={key} className="form-group">
                  <label className="form-label">{label}</label>
                  {isView ? <p className="text-sm mt-1">{d[key] || "—"}</p> :
                    <input type={key.includes("birthday") || key === "anniversary" ? "date" : "text"} className="input-default w-full" value={form[key] || ""} onChange={e => set(key, e.target.value)} />}
                </div>
              ))}
              {[
                ["Preferences", "preferences"],
                ["Gifting Notes", "gifting_notes"],
                ["Personal Context", "personal_context"],
              ].map(([label, key]) => (
                <div key={key} className="form-group col-span-2">
                  <label className="form-label">{label}</label>
                  {isView ? <p className="text-sm mt-1">{d[key] || "—"}</p> :
                    <textarea className="input-default w-full" value={form[key] || ""} onChange={e => set(key, e.target.value)} rows={2} />}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: 30% — Quick info, Signals, Digital */}
        <div className="lg:col-span-3 space-y-5">
          {isView && (
            <div className="card-flat p-5 border-l-2 border-l-[#FFEB00]/40">
              <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-[#A1A1AA]">Status</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {d.personal_contact && <span className="text-[10px] px-2 py-0.5 bg-[#8B5CF6]/15 text-[#8B5CF6] border border-[#8B5CF6]/30">Personal</span>}
                {d.office_contact && <span className="text-[10px] px-2 py-0.5 bg-[#3B82F6]/15 text-[#3B82F6] border border-[#3B82F6]/30">Office</span>}
                {d.track_signals && <span className="text-[10px] px-2 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30">Tracking</span>}
                {d.relationship_type && <span className="text-[10px] px-2 py-0.5 bg-[#2A2A2A] text-[#A1A1AA] border border-[#2A2A2A]">{d.relationship_type}</span>}
              </div>
              {d.last_interaction_date && (
                <div className="pt-3 border-t border-[#2A2A2A]">
                  <p className="text-xs text-[#71717A] uppercase tracking-wide mb-1">Last Interaction</p>
                  <p className="text-sm">
                    {new Date(d.last_interaction_date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                    <span className="text-xs text-[#71717A] ml-2">
                      ({d.last_interaction_direction === "sent" ? "you sent" : "received"})
                    </span>
                  </p>
                  {d.last_interaction_subject && (
                    <div className="mt-2 p-2 bg-[#0F0F0F] border border-[#2A2A2A]">
                      <p className="text-xs font-medium text-[#FFEB00] truncate">{d.last_interaction_subject}</p>
                      {d.last_interaction_snippet && <p className="text-xs text-[#A1A1AA] mt-1 line-clamp-2">{d.last_interaction_snippet}</p>}
                      {d.last_interaction_thread_id && (
                        <a href={`https://mail.google.com/mail/u/0/#inbox/${d.last_interaction_thread_id}`} target="_blank" rel="noreferrer" className="text-[10px] text-[#3B82F6] hover:underline mt-1 inline-block">Open in Gmail ↗</a>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Signals Panel (right side) */}
          {isView && contactId !== "new" && (
            <div className="card-flat p-5 border-l-2 border-l-[#FF4444]/40" data-testid="signals-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm uppercase tracking-wider text-[#A1A1AA]">Signals</h3>
                <span className="text-[10px] text-[#71717A]">{alerts.length + signals.length} total</span>
              </div>
              {alerts.length + signals.length === 0 ? (
                <p className="text-xs text-[#71717A]">No signals yet. Add keywords below to track news + LinkedIn mentions.</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {alerts.slice(0, 5).map(a => {
                    const src = a.source || "";
                    const icon = src.includes("linkedin") ? "🔗" : src.includes("gmail") ? "✉" : src.includes("news") ? "📰" : "●";
                    return (
                      <div key={a.alert_id} className={`p-2 border text-xs ${a.status === "new" ? "border-[#FF4444]/30 bg-[#1A0A0A]" : "border-[#2A2A2A]"}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span>{icon}</span>
                          {a.url ? (
                            <a href={a.url} target="_blank" rel="noreferrer" className="font-medium hover:text-[#FFEB00] hover:underline truncate flex-1">{a.title}</a>
                          ) : (
                            <span className="font-medium truncate flex-1">{a.title}</span>
                          )}
                        </div>
                        <p className="text-[11px] text-[#A1A1AA] line-clamp-2">{a.summary}</p>
                        <span className="text-[9px] text-[#71717A]">{new Date(a.date).toLocaleDateString()}</span>
                      </div>
                    );
                  })}
                  {signals.slice(0, 5).map(s => (
                    <div key={s.signal_id} className="p-2 border border-[#2A2A2A] text-xs">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[9px] px-1.5 py-0.5 bg-[#3B82F6]/15 text-[#3B82F6]">{s.signal_type?.replace("_", " ")}</span>
                        <span className="text-[9px] text-[#71717A]">{new Date(s.signal_date).toLocaleDateString()}</span>
                      </div>
                      <p className="text-[11px] text-[#A1A1AA] line-clamp-2">{s.summary}</p>
                    </div>
                  ))}
                </div>
              )}
              {/* Signal Keywords Input — inline editor */}
              <div className="mt-4 pt-3 border-t border-[#2A2A2A]">
                <label className="text-[10px] uppercase tracking-wide text-[#71717A] mb-1 block">Signal Keywords (comma-separated)</label>
                {editingKeywords ? (
                  <div className="space-y-2">
                    <input
                      autoFocus
                      className="input-default w-full text-xs"
                      value={keywordsDraft}
                      onChange={e => setKeywordsDraft(e.target.value)}
                      placeholder='e.g. "product launch", "funding round"'
                      data-testid="signal-keywords-input"
                      onKeyDown={e => { if (e.key === "Enter") saveKeywordsInline(); if (e.key === "Escape") setEditingKeywords(false); }}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={saveKeywordsInline}
                        disabled={keywordsSaving}
                        className="btn-primary text-[10px] px-2 py-1"
                        data-testid="keywords-save-btn"
                      >{keywordsSaving ? "Saving..." : "Save"}</button>
                      <button
                        onClick={() => setEditingKeywords(false)}
                        className="btn-secondary text-[10px] px-2 py-1"
                      >Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-xs flex-1 text-[#A1A1AA]">{d.signal_keywords || <span className="text-[#71717A]">Name + Company are used by default</span>}</p>
                    <button
                      onClick={() => { setKeywordsDraft(d.signal_keywords || ""); setEditingKeywords(true); }}
                      className="text-[10px] text-[#FFEB00] hover:underline"
                      data-testid="keywords-edit-btn"
                    >Edit</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Digital Links — Icon based */}
          <div className="card-flat p-5 border-l-2 border-l-[#3B82F6]/40">
            <h3 className="font-semibold mb-3 text-sm uppercase tracking-wider text-[#A1A1AA]">Digital</h3>
            {isView ? (
              <div className="flex flex-wrap gap-2" data-testid="digital-links-icons">
                {[
                  ["linkedin_url", "in", "#0077B5", (v) => v.startsWith("http") ? v : `https://${v}`],
                  ["instagram_url", "IG", "#E4405F", (v) => v.startsWith("http") ? v : `https://instagram.com/${v.replace("@","")}`],
                  ["facebook_url", "f", "#1877F2", (v) => v.startsWith("http") ? v : `https://facebook.com/${v}`],
                  ["x_url", "𝕏", "#E4E4E7", (v) => v.startsWith("http") ? v : `https://x.com/${v.replace("@","")}`],
                  ["whatsapp", "wa", "#25D366", (v) => `https://wa.me/${v.replace(/\D/g,"")}`],
                  ["telegram_id", "tg", "#0088cc", (v) => `https://t.me/${v.replace("@","")}`],
                  ["company_website", "🌐", "#71717A", (v) => v.startsWith("http") ? v : `https://${v}`],
                ].filter(([k]) => d[k]).map(([k, label, color, hrefFn]) => (
                  <a
                    key={k}
                    href={hrefFn(d[k])}
                    target="_blank"
                    rel="noreferrer"
                    className="w-9 h-9 flex items-center justify-center border border-[#2A2A2A] hover:border-[#FFEB00]/50 transition-colors text-sm font-bold"
                    style={{ color }}
                    title={`${k.replace("_", " ")}: ${d[k]}`}
                    data-testid={`digital-link-${k}`}
                  >{label}</a>
                ))}
                {!["linkedin_url","instagram_url","facebook_url","x_url","whatsapp","telegram_id","company_website"].some(k => d[k]) && (
                  <p className="text-xs text-[#71717A]">No links yet</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {[["LinkedIn", "linkedin_url"], ["Instagram", "instagram_url"], ["Facebook", "facebook_url"], ["X / Twitter", "x_url"], ["WhatsApp", "whatsapp"], ["Telegram", "telegram_id"], ["Website", "company_website"]].map(([label, key]) => (
                  <div key={key}>
                    <label className="text-[10px] uppercase tracking-wide text-[#71717A] block mb-0.5">{label}</label>
                    <input className="input-default w-full text-xs" value={form[key] || ""} onChange={e => set(key, e.target.value)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Chat */}
      {contactId !== "new" && (
        <div className="card-flat" data-testid="ai-chat-section">
          <button onClick={() => setChatOpen(!chatOpen)} className="w-full p-4 flex items-center justify-between hover:bg-[#1A1A1A]">
            <span className="font-semibold flex items-center gap-2"><MessageCircle size={18} className="text-[#FFEB00]" /> AI Chat — Ask about {d?.name || "this contact"}</span>
            {chatOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {chatOpen && (
            <div className="border-t border-[#2A2A2A]">
              <div className="p-4 max-h-72 overflow-y-auto space-y-3" data-testid="chat-messages">
                {chatMessages.length === 0 && <p className="text-sm text-[#71717A] text-center py-4">Ask anything about this contact, their company, or relationship history...</p>}
                {chatMessages.map((m, i) => (
                  <div key={i} className={`text-sm p-3 rounded ${m.role === "user" ? "bg-[#1A1A00] border border-[#FFEB00]/20 ml-12" : "bg-[#1A1A1A] border border-[#2A2A2A] mr-12"}`}>
                    <span className="text-[10px] text-[#71717A] block mb-1">{m.role === "user" ? "You" : "AI"}</span>
                    <p className="whitespace-pre-wrap">{m.content}</p>
                  </div>
                ))}
                {chatLoading && <div className="text-sm text-[#71717A] text-center py-2">Thinking...</div>}
              </div>
              <div className="p-3 border-t border-[#2A2A2A] flex gap-2">
                <input className="input-default flex-1" placeholder="Ask about this contact..." value={chatInput}
                  onChange={e => setChatInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") sendChat(); }} data-testid="chat-input" />
                <button onClick={sendChat} disabled={chatLoading || !chatInput.trim()} className="btn-primary px-4" data-testid="chat-send-btn"><Send size={16} /></button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== CSV IMPORT ====================
const CSVImport = () => {
  const [uploading, setUploading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [activeBatch, setActiveBatch] = useState(null);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/rios/import/batches`).then(r => setBatches(r.data.batches || []));
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const r = await axios.post(`${API}/rios/import/upload`, { csv_text: text, filename: file.name });
      if (r.data.success) {
        const batch = await axios.get(`${API}/rios/import/batch/${r.data.batch_id}`);
        setActiveBatch(batch.data.batch);
        setBatches(prev => [{ batch_id: r.data.batch_id, filename: file.name, total_rows: r.data.total_rows, status: "pending_review", created_at: new Date().toISOString() }, ...prev]);
      }
    } catch (err) { alert("Upload failed: " + (err.response?.data?.detail || err.message)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const loadBatch = async (batchId) => {
    const r = await axios.get(`${API}/rios/import/batch/${batchId}`);
    setActiveBatch(r.data.batch);
  };

  const approveAll = async () => {
    if (!activeBatch) return;
    setApproving(true);
    const actions = activeBatch.rows.map(r => ({
      row_index: r.row_index,
      action: r.action || r.contact_match?.suggested_action || "skip",
      merge_with: r.contact_match?.matched_contact_id,
      create_company: r.org_match?.match_type === "unknown",
    }));
    try {
      const r = await axios.post(`${API}/rios/import/batch/${activeBatch.batch_id}/approve`, { actions });
      alert(`Import complete: ${r.data.created} created, ${r.data.merged} merged, ${r.data.skipped} skipped`);
      setActiveBatch(null);
      axios.get(`${API}/rios/import/batches`).then(r => setBatches(r.data.batches || []));
    } catch (err) { alert("Approve failed: " + (err.response?.data?.detail || err.message)); }
    finally { setApproving(false); }
  };

  const updateRowAction = (idx, action) => {
    setActiveBatch(prev => ({
      ...prev,
      rows: prev.rows.map(r => r.row_index === idx ? { ...r, action } : r),
    }));
  };

  return (
    <div className="p-6 space-y-6" data-testid="csv-import">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Varela Round" }}>CSV Import</h1>
        <label className="btn-primary flex items-center gap-2 cursor-pointer" data-testid="upload-csv-btn">
          <Upload size={16} /> {uploading ? "Uploading..." : "Upload CSV"}
          <input type="file" accept=".csv" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>

      {/* Active batch review */}
      {activeBatch && (
        <div className="card-flat">
          <div className="p-4 border-b border-[#2A2A2A] flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Review: {activeBatch.filename}</h3>
              <p className="text-xs text-[#71717A]">{activeBatch.total_rows} rows | Status: {activeBatch.status}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setActiveBatch(null)} className="btn-secondary text-sm">Close</button>
              {activeBatch.status === "pending_review" && (
                <button onClick={approveAll} disabled={approving} className="btn-primary text-sm" data-testid="approve-import-btn">
                  {approving ? "Processing..." : "Approve & Import"}
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="table-dense w-full text-xs">
              <thead>
                <tr>
                  <th>#</th><th>Name</th><th>Email</th><th>Phone</th><th>Organization</th><th>Match</th><th>Org Match</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {activeBatch.rows?.map(row => (
                  <tr key={row.row_index} className={row.contact_match?.match_type === "exact" ? "bg-[#00C853]/5" : row.contact_match?.match_type === "possible" ? "bg-[#FFEB00]/5" : ""}>
                    <td>{row.row_index + 1}</td>
                    <td className="font-medium">{row.parsed.name}</td>
                    <td>{row.parsed.email || "—"}</td>
                    <td>{row.parsed.phone || "—"}</td>
                    <td>{row.parsed.organization || "—"}</td>
                    <td>
                      {row.contact_match?.match_type === "exact" && <span className="text-[#00C853]">Exact: {row.contact_match.matched_name}</span>}
                      {row.contact_match?.match_type === "possible" && <span className="text-[#FFEB00]">Possible: {row.contact_match.matched_name}</span>}
                      {row.contact_match?.match_type === "new" && <span className="text-[#71717A]">New</span>}
                    </td>
                    <td>
                      {row.org_match?.match_type === "company" && <span className="text-[#8B5CF6]">{row.org_match.company_name}</span>}
                      {row.org_match?.match_type === "brand" && <span className="text-[#3B82F6]">{row.org_match.brand_name}</span>}
                      {row.org_match?.match_type === "zoho_company" && <span className="text-[#00BCD4]">{row.org_match.company_name} (Zoho)</span>}
                      {row.org_match?.match_type === "unknown" && <span className="text-[#71717A]">New org</span>}
                    </td>
                    <td>
                      {activeBatch.status === "pending_review" ? (
                        <select className="input-default py-0.5 px-1 text-xs" value={row.action || row.contact_match?.suggested_action || "skip"}
                          onChange={e => updateRowAction(row.row_index, e.target.value)}>
                          <option value="create">Create New</option>
                          <option value="merge">Merge</option>
                          <option value="skip">Skip</option>
                        </select>
                      ) : <span className="text-[#71717A]">{row.status}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Past batches */}
      {!activeBatch && batches.length > 0 && (
        <div className="card-flat">
          <div className="p-4 border-b border-[#2A2A2A]"><h3 className="font-semibold">Import History</h3></div>
          <table className="table-dense w-full">
            <thead><tr><th>File</th><th>Rows</th><th>Status</th><th>Date</th><th></th></tr></thead>
            <tbody>
              {batches.map(b => (
                <tr key={b.batch_id}>
                  <td className="font-medium text-sm">{b.filename}</td>
                  <td>{b.total_rows}</td>
                  <td><span className={`text-xs px-2 py-0.5 ${b.status === "pending_review" ? "bg-[#FFEB00]/20 text-[#FFEB00]" : "bg-[#00C853]/20 text-[#00C853]"}`}>{b.status}</span></td>
                  <td className="text-xs text-[#71717A]">{new Date(b.created_at).toLocaleDateString()}</td>
                  <td><button onClick={() => loadBatch(b.batch_id)} className="btn-ghost text-xs"><Eye size={14} /> View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ==================== COMPANIES LIST ====================
const CompaniesList = () => {
  const [companies, setCompanies] = useState([]);
  const [zohoCompanies, setZohoCompanies] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    axios.get(`${API}/rios/companies`).then(r => { setCompanies(r.data.companies || []); setZohoCompanies(r.data.zoho_companies || []); });
  }, []);

  const addCompany = async () => {
    if (!newName) return;
    await axios.post(`${API}/rios/companies`, { name: newName });
    setNewName(""); setShowAdd(false);
    const r = await axios.get(`${API}/rios/companies`);
    setCompanies(r.data.companies || []);
  };

  return (
    <div className="p-6 space-y-4" data-testid="companies-list">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Varela Round" }}>Companies</h1>
        <button onClick={() => setShowAdd(!showAdd)} className="btn-primary flex items-center gap-2" data-testid="add-company-btn"><Plus size={16} /> Add Company</button>
      </div>
      {showAdd && (
        <div className="card-flat p-4 flex items-center gap-3">
          <input className="input-default flex-1" placeholder="Company name" value={newName} onChange={e => setNewName(e.target.value)} />
          <button onClick={addCompany} className="btn-primary text-sm">Save</button>
          <button onClick={() => setShowAdd(false)} className="btn-secondary text-sm">Cancel</button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-flat p-5">
          <h3 className="font-semibold mb-3">RIOS Companies ({companies.length})</h3>
          <div className="space-y-2">
            {companies.map(c => (
              <div key={c.company_id} className="flex items-center justify-between p-2 border border-[#2A2A2A] hover:border-[#8B5CF6]/50">
                <span className="text-sm font-medium">{c.name}</span>
                <span className="text-xs text-[#71717A]">{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
            ))}
            {companies.length === 0 && <p className="text-sm text-[#71717A]">No companies yet</p>}
          </div>
        </div>
        <div className="card-flat p-5">
          <h3 className="font-semibold mb-3">From Zoho Books ({zohoCompanies.length})</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {zohoCompanies.map(c => (
              <div key={c.company_id} className="flex items-center justify-between p-2 border border-[#2A2A2A]">
                <span className="text-sm">{c.name}</span>
                <span className="text-xs text-[#00BCD4]">Zoho</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== BRANDS LIST ====================
const BrandsList = () => {
  const [brands, setBrands] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCompany, setNewCompany] = useState("");

  useEffect(() => {
    axios.get(`${API}/rios/brands`).then(r => setBrands(r.data.brands || []));
  }, []);

  const addBrand = async () => {
    if (!newName) return;
    await axios.post(`${API}/rios/brands`, { name: newName, company_name: newCompany });
    setNewName(""); setNewCompany(""); setShowAdd(false);
    const r = await axios.get(`${API}/rios/brands`);
    setBrands(r.data.brands || []);
  };

  return (
    <div className="p-6 space-y-4" data-testid="brands-list">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Varela Round" }}>Brands</h1>
        <button onClick={() => setShowAdd(!showAdd)} className="btn-primary flex items-center gap-2"><Plus size={16} /> Add Brand</button>
      </div>
      {showAdd && (
        <div className="card-flat p-4 flex items-center gap-3">
          <input className="input-default flex-1" placeholder="Brand name" value={newName} onChange={e => setNewName(e.target.value)} />
          <input className="input-default flex-1" placeholder="Parent company (optional)" value={newCompany} onChange={e => setNewCompany(e.target.value)} />
          <button onClick={addBrand} className="btn-primary text-sm">Save</button>
          <button onClick={() => setShowAdd(false)} className="btn-secondary text-sm">Cancel</button>
        </div>
      )}
      <table className="table-dense w-full">
        <thead><tr><th>Brand</th><th>Company</th><th>Created</th></tr></thead>
        <tbody>
          {brands.map(b => (
            <tr key={b.brand_id}>
              <td className="font-medium">{b.name}</td>
              <td className="text-sm text-[#71717A]">{b.company_name || "—"}</td>
              <td className="text-xs text-[#71717A]">{new Date(b.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export { RIOSDashboard, ContactsList, ContactProfile, CSVImport, CompaniesList, BrandsList };
